import {
  getPlanningFinancialField,
  IJBPPlanning,
  isCanonicalPlanningRow,
  isPlanningBaselineRow,
  isPlanningCategoryTotal,
  isPlanningPlanRow,
} from 'partner360/entities/planning'
import {
  TKeyInitiativesFinancialData,
  TKeyInitiativesFinancialField,
} from 'partner360/features/keyInitiatives/model/financialData'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

const getOfferingTotalScope = (row: IJBPPlanning): string =>
  JSON.stringify([
    row.PartnerId,
    row.ProgramTypes,
    row.FYear,
    row.FQuarter,
    Boolean(row.IsTarget),
    Boolean(row.IsActuals),
  ])

export const buildKeyInitiativesFinancialData = (
  rows: IJBPPlanning[],
  fYear: number,
  isRollupMode: boolean
): TKeyInitiativesFinancialData[] => {
  const totals: TKeyInitiativesFinancialData = {}

  const financialRows = rows.filter(
    (row) =>
      isCanonicalPlanningRow(row, {
        isRollup: isRollupMode,
        excludeAuxiliaryRows: true,
        includeOfferingTotals: true,
      }) &&
      row.Category !== 'jbp-total' &&
      (Boolean(row.IsTotal) || isPlanningCategoryTotal(row))
  )
  const offeringTotalScopes = new Set(
    financialRows.filter((row) => row.IsTotal).map(getOfferingTotalScope)
  )

  // Match Cockpit's authoritative offering total: secondary hierarchies such as Premium/AI PC
  // overlap the main categories. Fall back to category totals only for scopes with no IsTotal row.
  financialRows
    .filter((row) => row.IsTotal || !offeringTotalScopes.has(getOfferingTotalScope(row)))
    .forEach((row) => {
      const prefix = isPlanningPlanRow(row, fYear)
        ? 'PlansCY'
        : isPlanningBaselineRow(row, fYear)
          ? 'ActualsPY'
          : isPlanningPlanRow(row, fYear - 1)
            ? 'PlansPY'
            : null

      if (!prefix || row.FQuarter < 1 || row.FQuarter > 5) return

      const quarter = row.FQuarter === 5 ? '' : `Q${row.FQuarter}`

      for (const metric of Object.values(ActualsValueTypes)) {
        for (const isLocalCurrency of [false, true]) {
          const sourceField = getPlanningFinancialField(metric, isLocalCurrency)
          if (!sourceField) continue

          const field =
            `${prefix}${quarter}${isLocalCurrency ? 'LC' : ''}${metric}` as TKeyInitiativesFinancialField
          totals[field] = (totals[field] ?? 0) + (Number(row[sourceField]) || 0)
        }
      }
    })

  return Object.keys(totals).length ? [totals] : []
}
